/**
 * Core types for the URL-driven faceted filtering system.
 * Defines the structure for filter operators, clauses, and filter sets.
 */

import { z } from 'zod';

/**
 * Supported filter operators:
 * - "is": OR logic across selected values (field:=[v1,v2,...])
 * - "not": Exclude any overlap with selected values (field:!=[v1,v2,...])
 */
export const OperatorSchema = z.enum(['is', 'not']);
export type Operator = z.infer<typeof OperatorSchema>;

/**
 * A single filter clause for one facet.
 * Example: { facet: "customer", operator: "is", values: ["maersk", "hapag"] }
 * Note: values can be an empty array to represent a filter with no selections yet
 */
export const FilterClauseSchema = z.object({
  facet: z.string().min(1),
  operator: OperatorSchema,
  values: z.array(z.string().min(1)),
});
export type FilterClause = z.infer<typeof FilterClauseSchema>;

/**
 * A complete filter set: array of clauses.
 * Clauses are ANDed together in Typesense queries.
 * Example: [
 *   { facet: "customer", operator: "is", values: ["maersk"] },
 *   { facet: "status", operator: "is", values: ["active", "paused"] }
 * ]
 */
export const FilterSetSchema = z.array(FilterClauseSchema);
export type FilterSet = z.infer<typeof FilterSetSchema>;
